// Live guidance vs drawn boxes: grade the ensemble members + deterministic
// aids against the user's Bayesian boxes (Feature B made real).
//
// (The model-spread "guidance envelope" cones that used to live here were
// retired 2026-09-15: the raw member spaghetti, toggled per source, is the
// uncertainty display now. The NHC cone is the only cone on the map.)

import type { Box } from "./types";
import type { EnsembleSet, StormBundle } from "../data/liveApi";

interface GuidancePoint {
  tau: number;
  lat: number;
  lon: number;
}

/**
 * All ensemble sets in a bundle, with back-compat: logged bundles from
 * before the multi-source suite carry GEFS in the legacy `ensemble` slot.
 */
export function ensembleSets(bundle: StormBundle): EnsembleSet[] {
  if (bundle.ensembles && bundle.ensembles.length > 0) return bundle.ensembles;
  if (bundle.ensemble && bundle.ensemble.members.length > 0) {
    return [{
      source: "GEFS", kind: "physics", label: "GEFS (NOAA physics)",
      ...bundle.ensemble,
    }];
  }
  return [];
}

// ── box transit ──────────────────────────────────────────────────────

/**
 * First forecast hour at which a guidance track ENTERS the box (an
 * outside→inside transition) strictly after `afterTau`, or null. Segments
 * between forecast points (6-12h apart) are sampled so a fast track can't
 * jump clean over a small box between two output times, and a track already
 * sitting inside the box at `afterTau` doesn't count as a new entry.
 */
export function firstTauInBox(points: GuidancePoint[], box: Box, afterTau = -1): number | null {
  let prevInside: boolean | null = null;
  const visit = (tau: number, lat: number, lon: number): number | null => {
    const inside = inBox(lat, lon, box);
    const entered = inside && prevInside !== true;
    prevInside = inside;
    return entered && tau > afterTau ? Math.round(tau) : null;
  };

  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (i > 0) {
      const a = points[i - 1];
      const steps = Math.max(1, Math.ceil(Math.max(Math.abs(p.lat - a.lat), Math.abs(p.lon - a.lon)) / 0.25));
      for (let s = 1; s < steps; s++) {
        const f = s / steps;
        const hit = visit(a.tau + (p.tau - a.tau) * f, a.lat + (p.lat - a.lat) * f, a.lon + (p.lon - a.lon) * f);
        if (hit != null) return hit;
      }
    }
    const hit = visit(p.tau, p.lat, p.lon);
    if (hit != null) return hit;
  }
  return null;
}

function inBox(lat: number, lon: number, box: Box): boolean {
  return box.lat_min <= lat && lat <= box.lat_max && box.lon_min <= lon && lon <= box.lon_max;
}

export interface EnsembleGrade {
  source: string;
  label: string;
  kind: "physics" | "ai";
  total: number;
  hitA: number;
  pctA: number;
  medianTauA: number | null;
  // ordered A→B among members that hit A (null when no Box B)
  hitAB: number | null;
  pctAB: number | null;
}

export interface AidsGrade {
  total: number;
  hitA: number;
  hitNames: string[];
}

export interface GuidanceGrade {
  ensembles: EnsembleGrade[]; // one grade per source (GEFS / ENS / AIFS)
  ensemble: EnsembleGrade | null; // first physics grade (legacy convenience)
  aids: AidsGrade | null;
}

/** Per-source ensemble strike fractions + deterministic-aid hits for the boxes. */
export function gradeGuidance(bundle: StormBundle, boxA: Box, boxB: Box | null): GuidanceGrade {
  const ensembles: EnsembleGrade[] = [];
  for (const set of ensembleSets(bundle)) {
    if (set.members.length === 0) continue;
    const tausA: number[] = [];
    let hitAB = 0;
    for (const m of set.members) {
      const tA = firstTauInBox(m.points, boxA);
      if (tA == null) continue;
      tausA.push(tA);
      if (boxB && firstTauInBox(m.points, boxB, tA) != null) hitAB++;
    }
    // a formation-matched set: members that never form the system never strike
    const total = Math.max(set.ens_size ?? set.members.length, set.members.length);
    const sorted = [...tausA].sort((x, y) => x - y);
    ensembles.push({
      source: set.source,
      label: set.label,
      kind: set.kind,
      total,
      hitA: tausA.length,
      pctA: total > 0 ? Math.round((tausA.length / total) * 100) : 0,
      medianTauA: sorted.length > 0 ? sorted[Math.floor(sorted.length / 2)] : null,
      hitAB: boxB ? hitAB : null,
      pctAB: boxB && tausA.length > 0 ? Math.round((hitAB / tausA.length) * 100) : boxB ? 0 : null,
    });
  }

  let aids: AidsGrade | null = null;
  const runs = Object.values(bundle.model_runs);
  if (runs.length > 0) {
    const hitNames: string[] = [];
    for (const run of runs) {
      if (firstTauInBox(run.points, boxA) != null) hitNames.push(run.tech_name || run.tech);
    }
    aids = { total: runs.length, hitA: hitNames.length, hitNames };
  }

  return {
    ensembles,
    ensemble: ensembles.find((e) => e.kind === "physics") ?? null,
    aids,
  };
}
